// Safety net for what a React boundary cannot see: throws outside render (event callbacks, timers,
// deck.gl listeners) and rejected promises with no catch, which is the typical case of a fetch that
// fails inside a `useEffect` with no handling.
//
// It only OBSERVES: no preventDefault, so the browser still prints the error and nothing is masked.
// The value is having a single sink (report.ts) to hang telemetry on the day it exists.
import { reportError } from './report'

// The listener REFERENCES are kept, not a plain boolean: without them they cannot be removed from
// `window`, and a "reset" that only lowered a flag would leave the old listener attached and
// duplicate every report on reinstall (the idempotency test caught exactly that).
let installed: {
  onError: (event: ErrorEvent) => void
  onRejection: (event: PromiseRejectionEvent) => void
} | null = null

/**
 * Attaches the global error listeners. Idempotent: calling it twice does not duplicate the report
 * (which matters because StrictMode mounts twice in dev and HMR re-runs the module).
 */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') {
    return
  }

  const onError = (event: ErrorEvent) => {
    reportError('window', event.error ?? event.message, {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    })
  }

  const onRejection = (event: PromiseRejectionEvent) => {
    reportError('promise', event.reason, { url: window.location.href })
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  installed = { onError, onRejection }
}

/** Tests only: DETACHES the listeners and leaves the module ready for a clean reinstall. */
export function resetGlobalErrorHandlersForTests(): void {
  if (installed && typeof window !== 'undefined') {
    window.removeEventListener('error', installed.onError)
    window.removeEventListener('unhandledrejection', installed.onRejection)
  }
  installed = null
}
